import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Box, Typography, Autocomplete, TextField, IconButton, Card, CardMedia, Button } from "@mui/material";
import { Delete, Upload as UploadIcon } from "@mui/icons-material";
import FormatTimestamp from "../FormatTimestamp/FormatTimestamp";
import ImportTimestampsDialog from "./ImportTimestampsDialog";

const AVATAR_BG = "/img/avatar_man_large.webp";

const TappingPointsTimestamp = forwardRef(
  (
    {
      items,
      removeTimestamp,
      stateProp,
      handleTimestampChange,
      tappingPoints,
      dense = false,
      onImportTimestamps,
      maxDurationSeconds,
    },
    ref
  ) => {
    // Refs for each timestamp row
    const minutesInputRefs = useRef([]);
    const tappingPointRefs = useRef([]);

    // Import dialog state
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      focusOnNewItem: index => {
        setTimeout(() => {
          const minutesInputContainer = minutesInputRefs.current[index];
          if (minutesInputContainer) {
            // For MUI TextField, the ref points to the InputBase div
            // We need to find the actual input element inside it
            const actualInput = minutesInputContainer.querySelector
              ? minutesInputContainer.querySelector("input")
              : minutesInputContainer;
            if (actualInput) {
              actualInput.focus();
              if (actualInput.select) {
                actualInput.select();
              }
            }
          }
        }, 100);
      },
    }));

    // Handle TAB from seconds field to tapping point field
    const handleSecondsTab = index => {
      setTimeout(() => {
        const tappingPointContainer = tappingPointRefs.current[index];
        if (tappingPointContainer) {
          // Find the actual input element inside the container
          const actualInput = tappingPointContainer.querySelector
            ? tappingPointContainer.querySelector("input")
            : tappingPointContainer;
          if (actualInput) {
            actualInput.focus();
          }
        }
      }, 50);
    };
    const handleKeyDown = (event, index) => {
      if (event.key === "Tab" && !event.shiftKey) {
        // Get the current input value to filter options
        const inputValue = event.target.value || "";
        const allOptions = generateImageOptions(index);

        // Filter options based on current input (same logic as Autocomplete)
        const filteredOptions = allOptions.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        );

        // If there's exactly one filtered option, auto-select it
        const currentValue = findSelectedOption(items[index], index);

        if (filteredOptions.length === 1) {
          const singleOption = filteredOptions[0];
          // Auto-select if no current value OR if the filtered option is different from current
          const shouldAutoSelect = !currentValue || currentValue.value !== singleOption.value;

          if (shouldAutoSelect) {
            event.preventDefault();

            // Select the single option
            handleTimestampChange(singleOption.value, stateProp, index, "tapping_point_id");
            handleTimestampChange(singleOption.image, stateProp, index, "image_url");

            // Move focus to next row's minutes field or Add button
            setTimeout(() => {
              const nextRowIndex = index + 1;
              const nextMinutesInput = minutesInputRefs.current[nextRowIndex];

              if (nextMinutesInput) {
                // For TextField, we might need to get the actual input
                const actualInput = nextMinutesInput.querySelector
                  ? nextMinutesInput.querySelector("input")
                  : nextMinutesInput;
                if (actualInput) {
                  actualInput.focus();
                }
              } else {
                // Focus on Add button (still use DOM query for button outside this component)
                const addButton = document.querySelector("[data-add-timestamp-button]");
                if (addButton) {
                  addButton.focus();
                }
              }
            }, 100);
          }
        } else {
          // If no single result or other conditions, use default behavior
          // But still move to next row when TAB is pressed from autocomplete
          setTimeout(() => {
            const nextRowIndex = index + 1;
            const nextMinutesInput = minutesInputRefs.current[nextRowIndex];

            if (nextMinutesInput) {
              // For TextField, we might need to get the actual input
              const actualInput = nextMinutesInput.querySelector
                ? nextMinutesInput.querySelector("input")
                : nextMinutesInput;
              if (actualInput) {
                actualInput.focus();
              }
            } else {
              // Focus on Add button (still use DOM query for button outside this component)
              const addButton = document.querySelector("[data-add-timestamp-button]");
              if (addButton) {
                addButton.focus();
              }
            }
          }, 50);
        }
      }
    };

    const generateImageOptions = index => {
      if (!Array.isArray(tappingPoints)) return [];
      return tappingPoints
        .filter(obj => obj && obj.tapping_point_name && obj.tapping_point_name !== "Default Tapping Point")
        .map(obj => ({
          value: obj.tapping_point_id,
          label: obj.tapping_point_name,
          image: obj.tapping_point_image_url,
          index,
        }));
    };

    const findSelectedOption = (item, index) => {
      if (!item.tapping_point_id) return null;
      const options = generateImageOptions(index);
      return options.find(opt => opt.value === item.tapping_point_id) || null;
    };

    const handleImport = importedTimestamps => {
      if (onImportTimestamps) {
        onImportTimestamps(importedTimestamps);
      }
    };

    return (
      <Box>
        {/* Import Button */}
        {items.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setImportDialogOpen(true)}
              size="small"
            >
              Import from Spreadsheet
            </Button>
          </Box>
        )}

        {items.map((item, index) => (
          <Card
            key={`timestamp-${index}`}
            variant="outlined"
            sx={{ mb: dense ? 1 : 2, p: dense ? 1 : 1.25, borderRadius: 1 }}
          >
            <Box display="flex" alignItems="center" gap={dense ? 1 : 2} flexWrap="wrap">
              {/* Timestamp */}
              <Box flex="0 0 140px">
                <FormatTimestamp
                  ref={el => (minutesInputRefs.current[index] = el)}
                  dense={dense}
                  value={item.timestamp}
                  handleChange={handleTimestampChange}
                  stateProp={stateProp}
                  indexToChange={index}
                  onSecondsTab={handleSecondsTab}
                />
              </Box>

              {/* Tapping Point Selector */}
              <Box flex="1 1 300px" minWidth={200}>
                <Autocomplete
                  fullWidth
                  options={generateImageOptions(index)}
                  getOptionLabel={option => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={findSelectedOption(item, index)}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleTimestampChange(newValue.value, stateProp, index, "tapping_point_id");
                      handleTimestampChange(newValue.image, stateProp, index, "image_url");
                    }
                  }}
                  renderInput={params => {
                    // Store ref callback for later use
                    const originalRef = params.InputProps?.ref;

                    return (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select tapping point"
                        onKeyDown={event => handleKeyDown(event, index)}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            ref: el => {
                              // Call original ref if exists (needed for Autocomplete)
                              if (originalRef) {
                                if (typeof originalRef === "function") {
                                  originalRef(el);
                                } else if (originalRef.current !== undefined) {
                                  originalRef.current = el;
                                }
                              }
                              // Store our ref
                              tappingPointRefs.current[index] = el;
                            },
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            paddingTop: "4px !important",
                            paddingBottom: "4px !important",
                          },
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              position: "relative",
                              width: dense ? 32 : 40,
                              height: dense ? 32 : 40,
                              borderRadius: 1,
                              overflow: "hidden",
                              bgcolor: "#FFFCF5",
                            }}
                          >
                            {/* Background image */}
                            <Box
                              component="img"
                              src={AVATAR_BG}
                              alt="Avatar background"
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                                pointerEvents: "none",
                              }}
                            />
                            {/* Tapping point image overlay */}
                            {option.image && (
                              <Box
                                component="img"
                                src={option.image}
                                alt={option.label}
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 2,
                                }}
                              />
                            )}
                          </Box>
                          <Typography>{option.label}</Typography>
                        </Box>
                      </li>
                    );
                  }}
                />
              </Box>

              {/* Delete Button */}
              <Box flex="0 0 40px" display="flex" justifyContent="flex-end" alignItems="center">
                <IconButton color="error" onClick={() => removeTimestamp(index)} size="small">
                  <Delete />
                </IconButton>
              </Box>
            </Box>
          </Card>
        ))}

        {/* Import Dialog */}
        <ImportTimestampsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleImport}
          tappingPoints={tappingPoints}
          maxDurationSeconds={maxDurationSeconds}
        />
      </Box>
    );
  }
);

export default TappingPointsTimestamp;
